/**
 * Client-side auth diagnostic helpers.
 * Generates a per-attempt correlation ID, forwards it to the backend via
 * X-Auth-Diag-ID, and produces a NON-SENSITIVE snapshot of the network/runtime state
 * to display when a login/register attempt fails.
 *
 * No password / cookie / JWT / secret is ever collected here.
 */
import { api, API_URL } from "@/lib/api";

export function newDiagId() {
  const d = new Date();
  const pad = (n, l = 2) => String(n).padStart(l, "0");
  const stamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `AUTH-${stamp}-${rand}`;
}

function bundleVersion() {
  try {
    const s = document.querySelector('script[src*="/static/js/main."]');
    if (!s) return "unknown";
    const m = /main\.([a-f0-9]+)\.js/.exec(s.src);
    return m ? m[1] : "unknown";
  } catch (_) { return "unknown"; }
}

function swInfo() {
  const info = { supported: false, controller: false, scriptURL: "", ready: false };
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return info;
  info.supported = true;
  const c = navigator.serviceWorker.controller;
  if (c) {
    info.controller = true;
    info.scriptURL = c.scriptURL || "";
  }
  return info;
}

export function snapshotEnv() {
  return {
    bundle: bundleVersion(),
    apiBase: `${API_URL}/api`,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    lang: (typeof navigator !== "undefined" && navigator.language) || null,
    ua: (typeof navigator !== "undefined" && (navigator.userAgent || "").slice(0, 140)) || null,
    sw: swInfo(),
    ts: new Date().toISOString(),
  };
}

/** Convert an axios error into a NON-SENSITIVE diagnostic dict. */
export function snapshotError(err) {
  const res = err && err.response;
  const backendCode = res && res.data && res.data.detail && typeof res.data.detail === "object" && !Array.isArray(res.data.detail)
    ? res.data.detail.code : null;
  return {
    name: err && err.name,
    message: err && err.message,
    code: err && err.code,
    hasResponse: !!res,
    status: res ? res.status : null,
    backendCode,
    diagIdReturned: res && res.headers ? res.headers["x-auth-diag-id"] || null : null,
  };
}

/**
 * Fire-and-forget reachability probe: GET /api/diag/echo with the same diag ID.
 * Used to prove whether the backend can be reached at all from this browser.
 */
export async function pingBackend(diagId) {
  try {
    const t0 = performance.now();
    const { data, status } = await api.get("/diag/echo", { headers: { "X-Auth-Diag-ID": diagId } });
    return { ok: true, status, ms: Math.round(performance.now() - t0), server_time: data?.server_time, diag_id: data?.diag_id };
  } catch (e) {
    return { ok: false, error: snapshotError(e) };
  }
}
